<template>
  <div class="container-fluid p-3">
    <div class="flex items-center justify-between">
      <div>
        <h3 class="h3 my-0 mx-0 text-foreground">Contacts</h3>
        <p class="text-muted-foreground text-sm">Track contacts through the standard review sequence</p>
      </div>
      <div class="flex items-center space-x-4">
        <router-link
          v-if="user && user.business"
          :to="{
            name: 'request-reviews',
            params: {
              /* eslint-disable-next-line vue/camelcase */
              unique_id: user.business.unique_id + '-' + user.business.id,
            },
          }"
          class="btn bg-primary space-x-3 round-xl px-4 request-review-btn"
          target="_blank"
          ><i class="uil uil-star"></i
          ><span class="text-sm font-medium"> Request Review</span>
        </router-link>
      </div>
    </div>
    <hr class="pb-4 pt-0" style="margin: 0 -40px 0 -20px !important" />
        <!-- Statistics Cards -->
        <div class="bg-card-50 rounded-2xl p-6 mb-6 border border-border-50">
          <div class="grid grid-cols-1 md-grid-cols-4 gap-6">
            <!-- Total Contacts Card -->
            <div class="stat-card">
              <div class="p-6">
                <div class="flex items-center justify-between">
                  <div>
                    <p class="text-sm font-medium text-muted-foreground">Total Contacts</p>
                    <p class="text-3xl font-bold">{{ totalContacts }}</p>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="stat-icon">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                </div>
                <p class="text-xs text-muted-foreground mt-2">In the system</p>
              </div>
            </div>

            <!-- Clicked Links Card -->
            <div class="stat-card">
              <div class="p-6">
                <div class="flex items-center justify-between">
                  <div>
                    <p class="text-sm font-medium text-muted-foreground">Clicked Links</p>
                    <p class="text-3xl font-bold">{{ clickedLinksCount }}</p>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="stat-icon">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                  </svg>
                </div>
                <p class="text-xs text-muted-foreground mt-2">Exited sequence</p>
              </div>
            </div>

            <!-- Click Rate Card -->
            <div class="stat-card">
              <div class="p-6">
                <div class="flex items-center justify-between">
                  <div>
                    <p class="text-sm font-medium text-muted-foreground">Click Rate</p>
                    <p class="text-3xl font-bold">{{ clickRate }}%</p>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="stat-icon">
                    <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                    <polyline points="16 7 22 7 22 13"></polyline>
                  </svg>
                </div>
                <p class="text-xs text-muted-foreground mt-2">Overall conversion</p>
              </div>
            </div>

            <!-- Completed Sequence Card -->
            <div class="stat-card">
              <div class="p-6">
                <div class="flex items-center justify-between">
                  <div>
                    <p class="text-sm font-medium text-muted-foreground">Completed Sequence</p>
                    <p class="text-3xl font-bold">{{ completedSequenceCount }}</p>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="stat-icon">
                    <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z"></path>
                  </svg>
                </div>
                <p class="text-xs text-muted-foreground mt-2">Never clicked</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Search and Filter Section -->
        <div class="bg-card-50 rounded-2xl p-6 mb-6 border border-border-50">
          <div class="flex flex-col sm-flex-row gap-4">
            <div class="relative flex-1">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="search-icon">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.3-4.3"></path>
              </svg>
              <input
                v-model="searchQuery"
                class="search-input"
                placeholder="Search contacts..."
                @input="handleSearch"
              />
            </div>
            <b-form-select
              v-model="filterStatus"
              class="filter-select"
              :options="filterOptions"
              @change="handleFilterChange"
            ></b-form-select>
          </div>
        </div>

        <!-- Contacts Table Section -->
        <div class="bg-card-50 rounded-2xl p-6 mb-6 border border-border-50">
          <h3 class="text-lg font-semibold text-foreground mb-4">Contact List ({{ totalContacts }})</h3>
          <div v-if="retrievingTableData" class="text-center py-8">
            <b-spinner small /> Retrieving Contacts
          </div>
          <div v-else-if="filteredContacts.length < 1" class="text-center py-8 text-muted-foreground">
            No Contacts
          </div>
          <div v-else class="rounded-md border">
            <div class="relative w-full overflow-auto">
              <table class="contacts-table">
                <thead>
                  <tr>
                    <th>Contact</th>
                    <th>Employee</th>
                    <th>Sequence Status</th>
                    <th>Click Data</th>
                    <th>Added</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="contact in filteredContacts" :key="contact.id" class="table-row">
                    <td class="p-4 align-middle">
                      <div>
                        <div class="font-medium">{{ contact.first_name }} {{ contact.last_name }}</div>
                        <div class="text-sm text-muted-foreground">{{ contact.email }}</div>
                        <div class="text-sm text-muted-foreground">({{ formatPhone(contact.cell_phone) }})</div>
                      </div>
                    </td>
                    <td class="p-4 align-middle">
                      <div class="font-medium">{{ getEmployeeName(contact) }}</div>
                    </td>
                    <td class="p-4 align-middle">
                      <span :class="getStatusBadgeClass(contact)">
                        {{ getSequenceStatus(contact) }}
                      </span>
                    </td>
                    <td class="p-4 align-middle">
                      <div v-if="hasClicked(contact)">
                        <div class="font-medium text-green-600">✓ {{ getClickType(contact) }}</div>
                        <div class="text-sm text-muted-foreground">{{ getClickDate(contact) }}</div>
                      </div>
                      <div v-else class="text-sm text-muted-foreground">
                        {{ contact.finished ? 'No clicks - completed sequence' : 'No clicks yet' }}
                      </div>
                    </td>
                    <td class="p-4 align-middle">
                      <div class="text-sm">{{ contact.front_date || formatDate(contact.created_at) }}</div>
                      <div class="text-xs text-muted-foreground">{{ getSource(contact) }}</div>
                    </td>
                    <td class="p-4 align-middle">
                      <button class="view-btn" @click="viewContact(contact)">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="eye-icon">
                          <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                        View Contact
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <!-- Pagination -->
          <div v-if="!retrievingTableData && filteredContacts.length" class="flex items-center justify-between mt-4 text-sm text-muted-foreground">
            <span>Total {{ totalContacts }} contacts</span>
            <div class="flex items-center space-x-2">
              <button
                class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-transparent hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3"
                :disabled="currentPage === 1"
                @click="currentPage > 1 && (currentPage--, getContacts())"
              >
                «
              </button>
              <div class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent hover:bg-secondary/80 request-review-btn" style="background-color: rgb(36, 99, 235); color: #ffffff;">
                {{ currentPage }}
              </div>
              <button
                class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-transparent hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3"
                :disabled="currentPage >= Math.ceil(totalContacts / perPage)"
                @click="currentPage < Math.ceil(totalContacts / perPage) && (currentPage++, getContacts())"
              >
                »
              </button>
            </div>
          </div>
        </div>

      <!-- View Contact Modal -->
      <b-modal
        v-model="viewModalOpen"
        :title="selectedContact ? `${selectedContact.first_name} ${selectedContact.last_name}` : 'Contact Details'"
        size="lg"
        :hide-footer="true"
      >
        <div v-if="selectedContact" class="contact-details">
          <table class="table table-bordered">
            <tbody>
              <tr>
                <td><strong>Name</strong></td>
                <td>{{ selectedContact.first_name }} {{ selectedContact.last_name }}</td>
              </tr>
              <tr>
                <td><strong>Email</strong></td>
                <td>{{ selectedContact.email }}</td>
              </tr>
              <tr>
                <td><strong>Phone</strong></td>
                <td>{{ formatPhone(selectedContact.cell_phone) }}</td>
              </tr>
              <tr>
                <td><strong>Series</strong></td>
                <td>{{ getSeriesNames(selectedContact) }}</td>
              </tr>
              <tr>
                <td><strong>Total Steps</strong></td>
                <td>{{ selectedContact.total_steps || 'N/A' }}</td>
              </tr>
              <tr>
                <td><strong>Current Step</strong></td>
                <td>{{ selectedContact.current_step || 'N/A' }}</td>
              </tr>
              <tr>
                <td><strong>Finished</strong></td>
                <td>{{ selectedContact.finished || selectedContact.deleted_at ? 'Yes' : 'No' }}</td>
              </tr>
              <tr>
                <td><strong>Next Run Time</strong></td>
                <td>
                  <span v-if="selectedContact.finished || selectedContact.deleted_at">Finished</span>
                  <span v-else-if="selectedContact.next_series_process_time_front_date">
                    {{ selectedContact.next_series_process_time_front_date }}
                  </span>
                  <span v-else>Pending</span>
                </td>
              </tr>
              <tr v-if="selectedContact.visits && selectedContact.visits.length">
                <td colspan="2" class="text-center bg-primary text-white"><strong>Visits</strong></td>
              </tr>
              <tr v-if="selectedContact.visits && selectedContact.visits.length">
                <td colspan="2" class="p-0">
                  <table class="table w-100 table-striped">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Step</th>
                        <th>Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="visit in selectedContact.visits" :key="visit.id">
                        <td>{{ visit.type }}</td>
                        <td>{{ visit.step }}</td>
                        <td>{{ visit.front_date }}</td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </b-modal>
  </div>
</template>

<script>
import { BSpinner, BFormSelect, BModal } from 'bootstrap-vue'
import axios from 'axios'
import '../../../design/custom/components/_dashboard.scss'

export default {
  name: 'Contacts',
  components: {
    BSpinner,
    BFormSelect,
    BModal,
  },
  data() {
    return {
      retrievingTableData: false,
      loadingStats: false,
      searchQuery: '',
      filterStatus: 'all',
      currentPage: 1,
      perPage: 10,
      viewModalOpen: false,
      selectedContact: null,
      stats: {
        total_contacts: 0,
        clicked_links: 0,
        click_rate: 0,
        completed_sequence: 0,
      },
      requestContactsDatatable: {
        queries: {
          per_page: 10,
          page: 1,
          status: 'active',
          search: null,
          sort_by: 'created_at',
          sort_desc: false,
        },
      },
      filterOptions: [
        { value: 'all', text: 'All Contacts' },
        { value: 'active', text: 'Active' },
        { value: 'finished', text: 'Finished' },
        { value: 'clicked', text: 'Clicked' },
      ],
    }
  },
  computed: {
    reviewContacts() {
      return this.$store.getters['contactRequests/all'] || []
    },
    totalContacts() {
      // Use API stats if available, otherwise fall back to store total
      return this.stats.total_contacts || this.$store.getters['contactRequests/total'] || 0
    },
    filteredContacts() {
      let contacts = [...this.reviewContacts]

      // Apply search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        contacts = contacts.filter(contact => {
          const name = `${contact.first_name || ''} ${contact.last_name || ''}`.toLowerCase()
          const email = (contact.email || '').toLowerCase()
          const phone = (contact.cell_phone || '').toLowerCase()
          return name.includes(query) || email.includes(query) || phone.includes(query)
        })
      }

      // Apply status filter
      if (this.filterStatus === 'finished') {
        contacts = contacts.filter(c => c.finished || c.deleted_at)
      } else if (this.filterStatus === 'clicked') {
        contacts = contacts.filter(c => this.hasClicked(c))
      } else if (this.filterStatus === 'active') {
        contacts = contacts.filter(c => !c.finished && !c.deleted_at)
      }

      return contacts
    },
    clickedLinksCount() {
      // Use API stats if available, otherwise calculate from contacts
      if (this.stats.clicked_links > 0) {
        return this.stats.clicked_links
      }
      return this.reviewContacts.filter(c => this.hasClicked(c)).length
    },
    completedSequenceCount() {
      // Use API stats if available, otherwise calculate from contacts
      if (this.stats.completed_sequence > 0) {
        return this.stats.completed_sequence
      }
      return this.reviewContacts.filter(c => c.finished || c.deleted_at).length
    },
    clickRate() {
      // Use API stats if available, otherwise calculate
      if (this.stats.click_rate > 0) {
        return this.stats.click_rate.toFixed(1)
      }
      if (this.totalContacts === 0) return '0.0'
      return ((this.clickedLinksCount / this.totalContacts) * 100).toFixed(1)
    },
    user() {
      return this.$store.getters['auth/user']
    },
  },
  watch: {
    currentPage() {
      // Scroll to top when page changes
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
  },
  mounted() {
    if (this.user && this.user.business) {
      this.fetchContactRequestStats()
      this.getContacts()
    }
  },
  methods: {
    async fetchContactRequestStats() {
      if (!this.user || !this.user.business) {
        return
      }

      this.loadingStats = true
      try {
        const response = await axios.get(
          `/businesses/${this.user.business.id}/contact-request-stats`
        )

        if (response.data.success && response.data.data) {
          const data = response.data.data
          this.stats = {
            total_contacts: data.total_contacts || 0,
            clicked_links: data.total_clicks || 0,
            click_rate: data.click_rate || 0,
            completed_sequence: data.finished_contacts || 0,
          }
        }
      } catch (error) {
        console.error('Error fetching contact request stats:', error)
        // Don't show error to user, just use computed values from contacts
      } finally {
        this.loadingStats = false
      }
    },
    getContacts() {
      if (!this.user || !this.user.business) {
        console.error('User or business not available')
        return
      }
      this.retrievingTableData = true
      
      // Update queries with current search and filter
      this.requestContactsDatatable.queries.search = this.searchQuery || null
      this.requestContactsDatatable.queries.page = this.currentPage
      this.requestContactsDatatable.queries.per_page = this.perPage
      
      this.$store
        .dispatch('contactRequests/getAll', {
          queries: this.requestContactsDatatable.queries,
          page: 'contacts',
          businessId: this.user.business.id,
          agencyId: this.user.business.agency_id || null,
        })
        .then(() => {
          this.retrievingTableData = false
          // Refresh stats after fetching contacts
          this.fetchContactRequestStats()
        })
        .catch((err) => {
          this.retrievingTableData = false
          console.error(err)
        })
    },
    handleSearch() {
      this.currentPage = 1
      this.requestContactsDatatable.queries.page = 1
      this.getContacts()
    },
    handleFilterChange() {
      this.currentPage = 1
      this.requestContactsDatatable.queries.page = 1
      this.getContacts()
    },
    formatPhone(phone) {
      if (!phone) return 'N/A'
      // Format phone number: (555) 123-4567
      const cleaned = phone.replace(/\D/g, '')
      if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
      }
      return phone
    },
    formatDate(date) {
      if (!date) return 'N/A'
      const d = new Date(date)
      return d.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
    },
    getEmployeeName(contact) {
      // Try to get employee name from contact data
      if (contact.employee && contact.employee.name) {
        return contact.employee.name
      }
      if (contact.employee_name) {
        return contact.employee_name
      }
      // Default or from series
      if (contact.review_request_series && contact.review_request_series.length > 0) {
        return contact.review_request_series[0].employee_name || 'N/A'
      }
      return 'N/A'
    },
    getSequenceStatus(contact) {
      if (contact.finished || contact.deleted_at) {
        return 'Sequence Complete'
      }
      if (this.hasClicked(contact)) {
        const clickType = this.getClickType(contact)
        if (clickType.includes('SMS')) {
          return 'Clicked SMS'
        } else if (clickType.includes('Email')) {
          return `Clicked ${clickType}`
        }
        return 'Clicked'
      }
      if (contact.current_step) {
        return `Email ${contact.current_step} Sent`
      }
      return 'Waiting'
    },
    getStatusBadgeClass(contact) {
      const status = this.getSequenceStatus(contact)
      if (status.includes('Clicked')) {
        return 'status-badge status-badge-green'
      } else if (status.includes('Email') && status.includes('Sent')) {
        return 'status-badge status-badge-purple'
      } else if (status === 'Sequence Complete') {
        return 'status-badge status-badge-gray'
      } else if (status === 'Waiting') {
        return 'status-badge status-badge-yellow'
      }
      return 'status-badge status-badge-gray'
    },
    hasClicked(contact) {
      return contact.visits && contact.visits.length > 0
    },
    getClickType(contact) {
      if (!this.hasClicked(contact)) return ''
      const lastVisit = contact.visits[contact.visits.length - 1]
      if (lastVisit.type === 'sms') {
        return 'Clicked SMS'
      } else if (lastVisit.type === 'email') {
        const step = lastVisit.step || ''
        return `Clicked Email ${step}`
      }
      return `Clicked ${lastVisit.type || ''}`
    },
    getClickDate(contact) {
      if (!this.hasClicked(contact)) return ''
      const lastVisit = contact.visits[contact.visits.length - 1]
      return lastVisit.front_date || this.formatDate(lastVisit.created_at)
    },
    getSource(contact) {
      // Try to determine source from contact data
      if (contact.source) {
        return contact.source
      }
      // Default sources based on common patterns
      return 'Website Form'
    },
    getSeriesNames(contact) {
      if (contact.review_request_series && contact.review_request_series.length > 0) {
        return contact.review_request_series.map(s => s.name).join(', ')
      }
      return 'N/A'
    },
    viewContact(contact) {
      this.selectedContact = contact
      this.viewModalOpen = true
    },
  },
}
</script>

<style lang="scss" scoped>
.flex-1 {
  flex: 1;
}

.flex {
  display: flex;
}

.flex-col {
  flex-direction: column;
}

.ml-4 {
  margin-left: 1rem;
}

.pb-6 {
  padding-bottom: 1.5rem;
}

.px-6 {
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}

.pt-3 {
  padding-top: 0.75rem;
}

.bg-background {
  background-color: #fff;
}

.border-b {
  border-bottom: 1px solid;
}

.border-border {
  border-color: #e5e7eb;
}

.px-8 {
  padding-left: 2rem;
  padding-right: 2rem;
}

.py-2 {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.-mx-6 {
  margin-left: -1.5rem;
  margin-right: -1.5rem;
}

.mb-6 {
  margin-bottom: 1.5rem;
}

.items-center {
  align-items: center;
}

.justify-between {
  justify-content: space-between;
}

.text-xl {
  font-size: 1.25rem;
}

.font-bold {
  font-weight: 700;
}

.text-foreground {
  color: #1f2937;
}

.text-muted-foreground {
  color: #6b7280;
}

.text-sm {
  font-size: 0.875rem;
}

.mt-1 {
  margin-top: 0.25rem;
}

.space-x-4 > * + * {
  margin-left: 1rem;
}

.request-review-btn {
  background-color: rgb(36, 99, 235) !important;
  color: #ffffff !important;
}


.bg-card-50 {
  background-color: rgba(255, 255, 255, 0.5);
}

.rounded-2xl {
  border-radius: 1rem;
}

.p-6 {
  padding: 1.5rem;
}

.border-border-50 {
  border-color: rgba(229, 231, 235, 0.5);
}

.grid {
  display: grid;
}

.grid-cols-1 {
  grid-template-columns: repeat(1, 1fr);
}

.md-grid-cols-4 {
  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
}

.gap-6 {
  gap: 1.5rem;
}

.stat-card {
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.stat-icon {
  width: 2rem;
  height: 2rem;
  color: #6b7280;
}

.text-3xl {
  font-size: 1.875rem;
}

.mt-2 {
  margin-top: 0.5rem;
}

.text-xs {
  font-size: 0.75rem;
}

.sm-flex-row {
  @media (min-width: 640px) {
    flex-direction: row;
  }
}

.gap-4 {
  gap: 1rem;
}

.relative {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1rem;
  height: 1rem;
  color: #6b7280;
  pointer-events: none;
}

.search-input {
  display: flex;
  height: 2.5rem;
  width: 100%;
  border-radius: 0.375rem;
  border: 1px solid #e5e7eb;
  background: #fff;
  padding: 0.5rem 0.75rem;
  padding-left: 2.5rem;
  font-size: 0.875rem;
  color: #1f2937;
  outline: none;
  transition: all 0.2s;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }

  &::placeholder {
    color: #9ca3af;
  }
}

.filter-select {
  height: 2.5rem;
  width: 10rem;
  border-radius: 0.375rem;
  border: 1px solid #e5e7eb;
  background: #fff;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  color: #1f2937;
  outline: none;
  transition: all 0.2s;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }
}

.text-lg {
  font-size: 1.125rem;
}

.font-semibold {
  font-weight: 600;
}

.mb-4 {
  margin-bottom: 1rem;
}

.rounded-md {
  border-radius: 0.375rem;
}

.border {
  border: 1px solid #e5e7eb;
}

.overflow-auto {
  overflow: auto;
}

.contacts-table {
  width: 100%;
  font-size: 0.875rem;
  border-collapse: collapse;

  thead {
    tr {
      border-bottom: 1px solid #e5e7eb;
    }
  }

  th {
    height: 3rem;
    padding: 0 1rem;
    text-align: left;
    vertical-align: middle;
    font-weight: 500;
    color: #6b7280;
    background: #f9fafb;
  }

  tbody {
    tr:last-child {
      border-bottom: none;
    }
  }

  .table-row {
    border-bottom: 1px solid #e5e7eb;
    transition: background-color 0.2s;

    &:hover {
      background-color: rgba(249, 250, 251, 0.5);
    }
  }

  td {
    padding: 1rem;
    vertical-align: middle;
  }
}

.font-medium {
  font-weight: 500;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  border: 1px solid transparent;
  padding: 0.125rem 0.625rem;
  font-size: 0.75rem;
  font-weight: 600;
  transition: all 0.2s;

  &.status-badge-green {
    background-color: #dcfce7;
    color: #166534;
    border-color: transparent;
  }

  &.status-badge-purple {
    background-color: #f3e8ff;
    color: #6b21a8;
    border-color: transparent;
  }

  &.status-badge-gray {
    background-color: #f3f4f6;
    color: #1f2937;
    border-color: transparent;
  }

  &.status-badge-yellow {
    background-color: #fef3c7;
    color: #92400e;
    border-color: transparent;
  }
}

.text-green-600 {
  color: #16a34a;
}

.view-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  white-space: nowrap;
  font-size: 0.875rem;
  font-weight: 500;
  height: 2.25rem;
  padding: 0 0.75rem;
  border-radius: 0.375rem;
  background: transparent;
  border: none;
  color: #1f2937;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #f3f4f6;
    color: #1f2937;
  }

  .eye-icon {
    width: 1rem;
    height: 1rem;
    margin-right: 0.5rem;
  }
}

.mt-4 {
  margin-top: 1rem;
}

.justify-between {
  justify-content: space-between;
}

.py-8 {
  padding-top: 2rem;
  padding-bottom: 2rem;
}

.text-center {
  text-align: center;
}

.contact-details {
  .table {
    width: 100%;
    margin-bottom: 0;

    td {
      padding: 0.75rem;
    }
  }
}
</style>

